/**
 * Small colour helpers so the whole palette can be derived from the two brand
 * colours in `src/config/brand.ts` instead of being hand-written per mode.
 */

type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };

const clamp = (v: number, min = 0, max = 1) => Math.min(max, Math.max(min, v));

export const hexToRgb = (hex: string): Rgb => {
    let h = hex.trim().replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return { r: 0, g: 0, b: 0 };
    const n = parseInt(h, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = ({ r, g, b }: Rgb): string =>
    '#' +
    [r, g, b]
        .map((v) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0'))
        .join('');

const rgbToHsl = ({ r, g, b }: Rgb): Hsl => {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return { h: 0, s: 0, l };

    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h: number;
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
    return { h, s, l };
};

const hslToRgb = ({ h, s, l }: Hsl): Rgb => {
    if (s === 0) {
        const v = l * 255;
        return { r: v, g: v, b: v };
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    const channel = (t: number) => {
        let x = t;
        if (x < 0) x += 1;
        if (x > 1) x -= 1;
        if (x < 1 / 6) return p + (q - p) * 6 * x;
        if (x < 1 / 2) return q;
        if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
        return p;
    };
    return {
        r: channel(h + 1 / 3) * 255,
        g: channel(h) * 255,
        b: channel(h - 1 / 3) * 255,
    };
};

export const toHsl = (hex: string): Hsl => rgbToHsl(hexToRgb(hex));

/** Return `hex` with lightness/saturation overridden or nudged. */
export const adjust = (
    hex: string,
    opts: { l?: number; s?: number; dl?: number; ds?: number }
): string => {
    const hsl = toHsl(hex);
    const l = clamp(opts.l ?? hsl.l + (opts.dl ?? 0));
    const s = clamp(opts.s ?? hsl.s + (opts.ds ?? 0));
    return rgbToHex(hslToRgb({ h: hsl.h, s, l }));
};

/** Blend two colours; `t` of 0 returns `a`, 1 returns `b`. */
export const mix = (a: string, b: string, t: number): string => {
    const ca = hexToRgb(a);
    const cb = hexToRgb(b);
    const k = clamp(t);
    return rgbToHex({
        r: ca.r + (cb.r - ca.r) * k,
        g: ca.g + (cb.g - ca.g) * k,
        b: ca.b + (cb.b - ca.b) * k,
    });
};

/** `rgba()` string for a hex colour at the given opacity. */
export const alpha = (hex: string, a: number): string => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${Math.round(r)},${Math.round(g)},${Math.round(b)},${clamp(a)})`;
};

/** WCAG relative luminance. */
export const luminance = (hex: string): number => {
    const { r, g, b } = hexToRgb(hex);
    const f = (v: number) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};

export const contrastRatio = (a: string, b: string): number => {
    const la = luminance(a);
    const lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
};

/**
 * Pick readable text for a background. Used for the app bar, whose gradient is
 * whatever the project configured — a light orange brand needs dark text where
 * a deep blue one needs white.
 */
export const contrastText = (hex: string, dark = '#0B1220', light = '#FFFFFF'): string =>
    contrastRatio(hex, light) >= contrastRatio(hex, dark) ? light : dark;

/**
 * Nudge a brand colour to a lightness that stays legible as an accent on the
 * given surface, without discarding its hue.
 */
export const toAccent = (hex: string, mode: 'light' | 'dark'): string => {
    const { l, s } = toHsl(hex);
    // Very desaturated brands (near-greys) get a small saturation floor so the
    // derived accent does not read as plain grey.
    const targetS = clamp(Math.max(s, 0.35), 0, 0.92);
    const targetL = mode === 'dark' ? clamp(Math.max(l, 0.56), 0.5, 0.72) : clamp(Math.min(l, 0.44), 0.3, 0.5);
    return adjust(hex, { l: targetL, s: targetS });
};
